/** /zh/security — Chinese version. */
import { MarketingShell } from '../MarketingShell';

export function SecurityZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/security"
      documentTitle="Draftlize 的安全 · Draftlize"
      documentDescription="Draftlize 如何保护你的数据。Fly.io 上的无状态容器、租户隔离的 Postgres、加密的 Tigris S3 存 refs、绝不用你数据训练模型。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">安全</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            Draftlize <em>的安全。</em>
          </h1>
          <p className="lede">
            人话版总结：我们怎么保护你的数据，不会用它做什么。没有假徽章；小团队；我们对当前状态和还没拿的认证都诚实说明。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">我们<em>做的事。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">租户隔离</h3>
            <p>每张卡、thread、ref 在数据库层用 <code>(tenant_id, project_id)</code> 作 key。应用代码无法跨租户查询——这是 schema 级的保证，不是"我们承诺会过滤"的保证。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">无状态容器</h3>
            <p>Draftlize server 作为无状态容器跑在 Fly.io 上。磁盘上无数据。重启、重发、区域故障转移——计算层不留任何东西。所有状态在 Postgres + Tigris S3，两者静态加密。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">仅 HTTPS + 安全 session cookie</h3>
            <p>每个请求都是 TLS 上的 HTTP/2。Session cookie 是 <code>HttpOnly</code>、<code>Secure</code>、<code>SameSite=Lax</code>，bearer token 静态哈希存储。</p>
          </div>
          <div className="problem-col">
            <span className="num">IV</span>
            <h3 className="serif">绝不用你的数据训练模型</h3>
            <p>我们不用你的卡片、thread、refs 训练模型。永远不。LLM 调用走上游 provider（目前 GLM、DeepSeek），都在不训练的 API 协议下。</p>
          </div>
          <div className="problem-col">
            <span className="num">V</span>
            <h3 className="serif">自建错误监控</h3>
            <p>不用第三方错误追踪（不 Sentry、不 Datadog）。错误存我们自己数据库的同一租户边界内。PII 永不离开你的租户。</p>
          </div>
          <div className="problem-col">
            <span className="num">VI</span>
            <h3 className="serif">每日加密备份</h3>
            <p>Postgres dump 每天到 Tigris S3 桶，静态加密，30 天保留。每月做恢复测试。你的数据导出端点随时拉自实时 DB。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">我们<em>还没拿到</em>的（诚实说）。</h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">A</span>
            <h3 className="serif">SOC 2</h3>
            <p>暂无 SOC 2 Type II。我们是小团队。如果你采购流程必须 SOC 2 才签字，那 Draftlize 现在不是合适的工具。12 个月 roadmap 上。</p>
          </div>
          <div className="problem-col">
            <span className="num">B</span>
            <h3 className="serif">GDPR DPA</h3>
            <p>数据处理协议按需提供——邮件 <a href="mailto:hi@draftlize.com">hi@draftlize.com</a>。标准 EU SCC 模板，48 小时内签好。</p>
          </div>
          <div className="problem-col">
            <span className="num">C</span>
            <h3 className="serif">SSO / SAML</h3>
            <p>暂无——目前是 GitHub OAuth + Google OAuth + 邮件 magic-link。SSO 在 roadmap 上，团队套餐落地时一起。</p>
          </div>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">事故响应。<em>我们会怎么通知你。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">如果我们发现 breach</h3>
            <p>确认后 72 小时内邮件每个受影响用户。7 天内公开 status 帖。说清发生了什么、暴露了什么、我们改了什么——人话。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">如果你发现漏洞</h3>
            <p>邮件 <a href="mailto:security@draftlize.com">security@draftlize.com</a>。48 小时内回复。暂无 bug bounty（小团队），但如果报告真实且重要，我们会公开致谢。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">如果你想验证我们的说法</h3>
            <p>我们做 30 分钟通话走过 codebase 架构。邮件 <a href="mailto:hi@draftlize.com">hi@draftlize.com</a> 如果你在为有意义的支出评估 Draftlize 需要检查。</p>
          </div>
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">更多问题？<em>邮件我们。</em></h2>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            <a href="mailto:hi@draftlize.com">hi@draftlize.com</a> 一般问题。<a href="mailto:security@draftlize.com">security@draftlize.com</a> 漏洞报告。真人，快速回复。
          </p>
          <a className="btn-primary" href="/">用 $5 免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
